/**
 * Login Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getChatGPTConsentReturnTo } from '../lib/chatgptConsent';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { MFAChallenge } from '../components/auth/MFAChallenge';
import { AuthCardTitle, AuthShell } from '../components/auth/AuthShell';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset Password State
  const [resetEmail, setResetEmail] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetSentEmail, setResetSentEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const { signIn, signInWithGoogle, resetPassword } = useAuth();
  const location = useLocation();
  const returnTo = getChatGPTConsentReturnTo(new URLSearchParams(location.search).get('returnTo'));

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn(email, password);
      // Check emailVerified on the credential directly (not stale React state)
      if (!result.user.emailVerified) {
        navigate(`/verify-email?returnTo=${encodeURIComponent(returnTo)}`, { state: { fromLogin: true } });
        return;
      }
      navigate(returnTo);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      // signInWithPopup returns immediately with credentials
      // If popup is blocked, it falls back to redirect
      const result = await signInWithGoogle();
      // If we get here, popup succeeded - navigate to dashboard
      if (result && result.user) {
        console.log('[Login] Google sign-in successful, navigating to dashboard');
        navigate(returnTo);
      }
      // If redirect fallback was used, page will navigate away
      // and onAuthStateChanged will handle navigation on return
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setError('');

    try {
      await resetPassword(resetEmail);
      setResetSentEmail(resetEmail);
      setResetSuccess(true);
      setResendCooldown(60);
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResendReset = async () => {
    if (resendCooldown > 0 || !resetSentEmail) return;
    setResetLoading(true);
    try {
      await resetPassword(resetSentEmail);
      setResendCooldown(60);
    } catch (err: any) {
      console.error('Resend reset email error:', err);
      setError(err.message || 'Failed to resend reset email');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <AuthShell>
      <Card className="w-full border-border bg-card shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
        <CardHeader className="space-y-3 pb-6">
          <AuthCardTitle className="text-4xl font-normal text-foreground">
            Welcome back
          </AuthCardTitle>
          <CardDescription className="max-w-sm text-base text-muted-foreground">
            Sign in to continue your firm&apos;s diligence and decision record.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && !resetOpen && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">Email Address</Label>
              <Input
                id="email"
                data-testid="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="h-11 transition-smooth focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
                <Dialog open={resetOpen} onOpenChange={(open) => {
                  setResetOpen(open);
                  setResetSuccess(false);
                  setError('');
                  setResendCooldown(0);
                  setResetSentEmail('');
                  if (open && email) setResetEmail(email);
                }}>
                  <DialogTrigger asChild>
                    <Button variant="link" className="px-0 font-normal text-xs text-primary h-auto">
                      Forgot password?
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="quantum-app">
                    <DialogHeader>
                      <DialogTitle>Reset Password</DialogTitle>
                      <DialogDescription>
                        Enter your email address and we'll send you a link to reset your password.
                      </DialogDescription>
                    </DialogHeader>

                    {resetSuccess ? (
                      <div className="py-4 text-center space-y-4">
                        <div className="h-12 w-12 bg-green-500/10 text-green-600 rounded-full flex items-center justify-center mx-auto">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          Reset link sent to <strong>{resetSentEmail}</strong>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Don't see it? Check your spam or junk folder. The link expires in 1 hour.
                        </p>
                        <div className="flex flex-col gap-2">
                          <Button onClick={() => setResetOpen(false)} className="w-full">
                            Back to Login
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleResendReset}
                            disabled={resendCooldown > 0 || resetLoading}
                            className="text-xs"
                          >
                            {resetLoading ? (
                              <span className="flex items-center gap-1">
                                <svg className="h-3 w-3 animate-spin motion-reduce:animate-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Sending...
                              </span>
                            ) : resendCooldown > 0 ? (
                              `Resend available in ${resendCooldown}s`
                            ) : (
                              'Resend reset link'
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleResetPassword} className="space-y-4 py-4">
                        {error && (
                          <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                          </Alert>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="resetEmail">Email Address</Label>
                          <Input
                            id="resetEmail"
                            type="email"
                            placeholder="you@company.com"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            required
                            disabled={resetLoading}
                          />
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={resetLoading} className="w-full">
                            {resetLoading ? (
                              <span className="flex items-center gap-2">
                                <svg className="h-4 w-4 animate-spin motion-reduce:animate-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Sending...
                              </span>
                            ) : (
                              'Send Reset Link'
                            )}
                          </Button>
                        </DialogFooter>
                      </form>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
              <Input
                id="password"
                data-testid="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="h-11 transition-smooth focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin motion-reduce:animate-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-card text-muted-foreground font-medium">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 font-medium border-border hover:bg-muted hover:border-muted-foreground/20 transition-smooth"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:text-primary/80 font-semibold transition-smooth hover:underline">
              Sign up
            </Link>
          </p>
        </CardContent>
      </Card>
      </AuthShell>
      <MFAChallenge />
    </>
  );
};
